import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import apiClient from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import TestimonialsSection from './TestimonialsSection';

export default function ServiceReviewsSection({ title, testimonials = [] }) {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const dialog = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [qr, setQr] = useState('');
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [success, setSuccess] = useState(false);
  const requested = params.get('review') === 'service';
  const reviewUrl = new URL('/?review=service#service-reviews', import.meta.env.VITE_PUBLIC_SITE_URL || window.location.origin).href;

  useEffect(() => {
    let active = true;
    apiClient.get('/service-reviews').then(({ data }) => {
      if (active) setReviews(data.reviews);
    }).catch(() => { if (active) setLoadError(true); });
    QRCode.toDataURL(reviewUrl, { width: 200, margin: 4, errorCorrectionLevel: 'M' })
      .then(value => { if (active) setQr(value); }).catch(() => {});
    return () => { active = false; };
  }, [reviewUrl]);

  useEffect(() => {
    if (!requested) {
      if (dialog.current.open) dialog.current.close();
      return;
    }
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { from: { pathname: '/', search: '?review=service', hash: '#service-reviews' } } });
    } else if (!dialog.current.open) {
      setSuccess(false);
      dialog.current.showModal();
    }
  }, [requested, isAuthenticated, isLoading, navigate]);

  const open = () => setParams(previous => {
    const next = new URLSearchParams(previous);
    next.set('review', 'service');
    return next;
  });
  const close = () => {
    dialog.current.close();
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('review');
      return next;
    }, { replace: true });
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!rating || !text.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await apiClient.post('/service-reviews', { rating, text: text.trim() });
      setReviews(current => [data.review, ...current.filter(review => review._id !== data.review._id)].slice(0, 30));
      setLoadError(false);
      setSuccess(true);
      setText('');
      setRating(0);
    } catch (err) {
      if (err.status === 401) {
        useAuthStore.setState({ user: null, token: null, isAuthenticated: false });
      } else setError(err.message || 'Unable to save your review. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <section id="service-reviews" className="bg-gray-50 py-12 md:py-16 scroll-mt-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">{title || 'What Our Customers Say'}</h2>
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0 w-full">
            <TestimonialsSection embedded testimonials={[...reviews, ...testimonials]} />
            {loadError && <p role="status" className="text-gray-600">Customer reviews could not be loaded. Please refresh to try again.</p>}
            {!loadError && !reviews.length && !testimonials.length && <p className="text-gray-600">Be the first to share your experience with our service.</p>}
          </div>
          <aside className="w-full lg:w-64 shrink-0 bg-white rounded-2xl border border-gray-100 p-5 text-center">
            <h3 className="font-bold text-lg text-gray-900">How was our service?</h3>
            <p className="text-sm text-gray-600 mt-2">Scan to share your experience.</p>
            <a href="/?review=service#service-reviews" onClick={event => { event.preventDefault(); open(); }} aria-label="Write a service review" className="block mx-auto w-fit">
              {qr && <img src={qr} width="200" height="200" alt="Scan to write a Patel Sales service review" />}
            </a>
            <button type="button" onClick={open} className="mt-3 w-full rounded-lg bg-[#003087] text-white font-semibold px-4 py-3">Write a review</button>
            <p className="text-xs text-gray-500 mt-3">Sign in to submit your review.</p>
          </aside>
        </div>
      </div>
      <dialog ref={dialog} aria-labelledby="service-review-title" onCancel={event => { event.preventDefault(); if (!saving) close(); }} className="w-[calc(100%-2rem)] max-w-lg rounded-2xl p-6 shadow-xl backdrop:bg-black/50">
        <div className="flex items-center justify-between gap-4 mb-5">
          <h2 id="service-review-title" className="text-xl font-bold">Review our service</h2>
          <button type="button" onClick={close} disabled={saving} aria-label="Close review form" className="text-2xl px-2">×</button>
        </div>
        {success ? <div role="status"><p className="text-green-700 mb-5">Thank you! Your review has been saved.</p><button type="button" onClick={close} className="bg-[#003087] text-white rounded-lg px-5 py-2">Done</button></div> : (
          <form onSubmit={submit} className="space-y-5">
            <div><label htmlFor="review-name" className="block font-medium mb-2">Your name</label><input id="review-name" value={user?.name || ''} readOnly className="w-full border rounded-lg p-3 bg-gray-50" /></div>
            <fieldset><legend className="font-medium mb-2">Rating out of 5</legend><div className="flex gap-3">
              {[1, 2, 3, 4, 5].map(value => <label key={value} className="cursor-pointer"><input type="radio" name="rating" value={value} checked={rating === value} onChange={() => setRating(value)} required className="peer sr-only" aria-label={`${value} star${value > 1 ? 's' : ''}`} /><span aria-hidden="true" className={`text-4xl peer-focus-visible:outline peer-focus-visible:outline-2 ${value <= rating ? 'text-amber-400' : 'text-gray-300'}`}>★</span></label>)}
            </div></fieldset>
            <div><label htmlFor="service-review-text" className="block font-medium mb-2">Your review</label><textarea id="service-review-text" value={text} onChange={event => setText(event.target.value)} required maxLength={2000} rows={4} className="w-full border rounded-lg p-3" placeholder="Tell us about your experience" /></div>
            {error && <p role="alert" className="text-red-700">{error}</p>}
            <button type="submit" disabled={saving || !rating || !text.trim()} className="w-full bg-[#003087] text-white rounded-lg py-3 font-semibold disabled:opacity-50">{saving ? 'Submitting…' : 'Submit review'}</button>
          </form>
        )}
      </dialog>
    </section>
  );
}
